// The signature "membership card" component — used in three places:
//
//   1. Create page sticky preview (renders from form state)
//   2. Detail page hero (renders from fetched plan)
//   3. Customer 360 Memberships tab (renders the customer's active plan)
//
// Designed as a physical-looking tier card with a gold-foil shine on hover,
// tabular-nums price, and metallic gradient sourced from tierGradient().
// Accepts a Partial<MembershipPlan> so it degrades gracefully on the create
// page where fields are still being typed.

import { memo } from 'react';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { MembershipPlan, MembershipTier } from './types';
import { formatDuration, formatTierLabel, listBenefits, tierGradient, tierShadow } from './format';
import { MembershipStatusPill } from './MembershipStatusPill';

export interface MembershipPreviewCardProps {
  plan: Partial<MembershipPlan> & { tier_level: MembershipTier };
  /** Controls the hover-shine + hover-lift animation (off on static detail pages). */
  interactive?: boolean;
  /** "preview" on the create page, "live" on detail/list, "compact" in the customer tab. */
  variant?: 'preview' | 'live' | 'compact';
  className?: string;
}

const BENEFIT_ICONS: Record<'discount' | 'points' | 'priority' | 'renewal', React.ReactNode> = {
  discount: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
    </svg>
  ),
  points: (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2l2.39 7.36H22l-6.19 4.5 2.39 7.36L12 16.72 5.8 21.22l2.39-7.36L2 9.36h7.61L12 2z" />
    </svg>
  ),
  priority: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  renewal: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
};

function MembershipPreviewCardImpl({ plan, interactive = true, variant = 'preview', className = '' }: MembershipPreviewCardProps) {
  const currency = useTenantCurrency();
  const tier = plan.tier_level;
  const name = plan.name?.trim() || 'Untitled Plan';
  const fee = Number.isFinite(plan.fee) ? (plan.fee as number) : 0;
  const durationDays = Number.isFinite(plan.duration_days) ? (plan.duration_days as number) : 0;
  const duration = formatDuration(durationDays);
  const benefits = listBenefits({
    discount_percentage: plan.discount_percentage ?? 0,
    points_multiplier: plan.points_multiplier ?? 1,
    priority_service: plan.priority_service ?? false,
    auto_renewal: plan.auto_renewal ?? false,
  });

  const padding = variant === 'compact' ? 'p-6' : 'p-7 sm:p-8';
  const priceSize =
    variant === 'compact' ? 'text-3xl' : variant === 'live' ? 'text-4xl sm:text-5xl' : 'text-4xl';

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${tierGradient(tier)} ${padding} text-white ${tierShadow(tier)} ${interactive ? 'transition-all duration-300 hover:-translate-y-1' : ''} ${className}`.trim()}
    >
      {/* Blurred orbs for Glass Depth */}
      <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />

      {/* Gold-foil hover shine (only on interactive variants) */}
      {interactive ? (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-white/0 via-white/25 to-white/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      ) : null}

      <div className="relative">
        {/* Tier label — replaces a traditional card brand */}
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/85">
          {formatTierLabel(tier)} Membership
        </p>

        {/* Plan name */}
        <h3 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight drop-shadow-sm line-clamp-2">
          {name}
        </h3>

        {/* Price + duration */}
        <div className="mt-5 flex items-baseline gap-2">
          <span className={`${priceSize} font-bold tracking-tight tabular-nums drop-shadow-sm`}>
            {formatPaise(fee, currency)}
          </span>
          <span className="text-sm text-white/80 tabular-nums">/ {duration.short}</span>
        </div>

        {/* Status + benefit count */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <MembershipStatusPill isActive={plan.is_active ?? true} />
          {benefits.length > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur-sm px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white ring-1 ring-white/25 tabular-nums">
              {benefits.length} benefit{benefits.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>

        {/* Benefits list */}
        {benefits.length > 0 ? (
          <ul className="mt-5 space-y-2">
            {benefits.slice(0, 5).map((b) => (
              <li key={b.icon} className="flex items-start gap-2.5 text-sm text-white/95">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20 text-white ring-1 ring-white/25">
                  {BENEFIT_ICONS[b.icon]}
                </span>
                <span className="leading-snug">{b.label}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-5 text-xs italic text-white/70">
            Add benefits in the form to preview them here.
          </p>
        )}

        {/* Description — small italic line at the bottom */}
        {plan.description?.trim() ? (
          <p className="mt-5 border-t border-white/15 pt-4 text-xs leading-relaxed text-white/80">
            {plan.description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Memoised so the live preview on the create page doesn't re-render on every
 * unrelated keystroke — only when its own plan prop shallow-changes.
 */
export const MembershipPreviewCard = memo(MembershipPreviewCardImpl);
